import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, FileDownIcon, ClipboardListIcon, XIcon, CheckCircleIcon, FileTextIcon, HistoryIcon, CreditCardIcon, PrinterIcon, LockIcon, TrashIcon, FileUpIcon, RefreshCwIcon } from 'lucide-react';
import BatchTicketEntryModal from '../components/modals/BatchTicketEntryModal';
import SingleTicketEntryModal from '../components/modals/SingleTicketEntryModal';
import EditTicketModal from '../components/modals/EditTicketModal';
import ViewTicketModal from '../components/modals/ViewTicketModal';
import AddATCModal from '../components/modals/AddATCModal';
import ConfirmDeleteModal from '../components/modals/ConfirmDeleteModal';
import StatusBadge from '../components/common/StatusBadge';
import PermissionBanner from '../components/common/PermissionBanner';
import EmptyState from '../components/common/EmptyState';
import ReconciliationTable from '../components/reconciliation/ReconciliationTable';
import ReconciliationSummary from '../components/reconciliation/ReconciliationSummary';
const TicketManagement = () => {
  const {
    gsId
  } = useParams();
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [isSingleTicketModalOpen, setIsSingleTicketModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddATCModalOpen, setIsAddATCModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteATCModalOpen, setIsDeleteATCModalOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [reconciliationStatus, setReconciliationStatus] = useState<'pending' | 'partial' | 'complete'>('pending');
  // Mock GS data
  const [groupData, setGroupData] = useState({
    id: gsId,
    tourCode: 'HKGTOUR23',
    refNo: '12XCAI1207',
    createdBy: 'John Doe',
    createdAt: '2023-06-15',
    status: 'Open',
    reconciliationStatus: 'pending',
    totalTickets: 2,
    totalAmount: 3000
  });
  // Mock ticket data
  const [tickets, setTickets] = useState([{
    id: 1,
    ticketNumber: '1601234567890',
    paxName: 'LOH/POHLEE MS',
    sector: 'SIN/CAI/SIN',
    sectorCode: 'SINCAI',
    airlineCode: 'QR',
    class: 'G',
    issueDate: '2023-06-15',
    airFare: 1200.0,
    tax: 300.0,
    totalCost: 1500.0,
    reconciled: true
  }, {
    id: 2,
    ticketNumber: '1601234567891',
    paxName: 'WONG/MARY MRS',
    sector: 'SIN/CAI/SIN',
    sectorCode: 'SINCAI',
    airlineCode: 'QR',
    class: 'G',
    issueDate: '2023-06-15',
    airFare: 1200.0,
    tax: 300.0,
    totalCost: 1500.0,
    reconciled: false
  }]);
  // Mock billing data for reconciliation
  const [billingEntries, setBillingEntries] = useState([{
    id: 1,
    ticketNumber: '1601234567890',
    amount: 1500.0,
    billingDate: '2023-06-16',
    billingRef: 'B-20230616-001',
    matched: true
  }, {
    id: 2,
    ticketNumber: '1601234567891',
    amount: 1450.0,
    billingDate: '2023-06-16',
    billingRef: 'B-20230616-002',
    matched: false
  }, {
    id: 3,
    ticketNumber: '1601234567892',
    amount: 980.0,
    billingDate: '2023-06-16',
    billingRef: 'B-20230616-003',
    matched: false
  }]);
  // Tabs state
  const [activeTab, setActiveTab] = useState('ticket');
  // Mock process log data
  const [processLogs, setProcessLogs] = useState([{
    id: 1,
    action: 'Group Created',
    timestamp: '2023-06-15 09:30:22',
    user: 'John Doe',
    details: 'Created ticketing group with ID TG-2023-001'
  }, {
    id: 2,
    action: 'Ticket Added',
    timestamp: '2023-06-15 09:35:10',
    user: 'John Doe',
    details: 'Added ticket 1601234567890 for LOH/POHLEE MS'
  }, {
    id: 3,
    action: 'Ticket Added',
    timestamp: '2023-06-15 09:36:45',
    user: 'John Doe',
    details: 'Added ticket 1601234567891 for WONG/MARY MRS'
  }, {
    id: 4,
    action: 'Reconciliation',
    timestamp: '2023-06-18 14:22:33',
    user: 'Jane Smith',
    details: 'Reconciled ticket 1601234567890 with billing'
  }]);
  // Mock ATC data
  const [atcData, setAtcData] = useState([{
    id: 1,
    atcCode: 'YQ',
    description: 'Fuel Surcharge',
    amount: 200.0
  }, {
    id: 2,
    atcCode: 'YR',
    description: 'Insurance Surcharge',
    amount: 50.0
  }, {
    id: 3,
    atcCode: 'SG',
    description: 'Singapore Departure Tax',
    amount: 30.0
  }]);
  // Toggle lock status
  const handleToggleLock = () => {
    setIsLocked(!isLocked);
    setGroupData(prev => ({
      ...prev,
      status: !isLocked ? 'Locked' : 'Open'
    }));
    // Add to process log
    addProcessLog({
      action: !isLocked ? 'Group Locked' : 'Group Unlocked',
      details: !isLocked ? `Locked ticket group ${gsId}` : `Unlocked ticket group ${gsId}`
    });
  };
  // Handle adding a new ticket
  const handleAddTicket = ticketData => {
    const newTicket = {
      id: tickets.length + 1,
      ...ticketData,
      reconciled: false
    };
    setTickets([...tickets, newTicket]);
    // Update group totals
    setGroupData(prev => ({
      ...prev,
      totalTickets: prev.totalTickets + 1,
      totalAmount: prev.totalAmount + ticketData.totalCost
    }));
    // Add to process log
    addProcessLog({
      action: 'Ticket Added',
      details: `Added ticket ${ticketData.ticketNumber} for ${ticketData.paxName}`
    });
    // Update reconciliation status
    updateReconciliationStatus();
  };
  // Handle updating a ticket
  const handleUpdateTicket = updatedTicket => {
    setTickets(tickets.map(ticket => ticket.id === updatedTicket.id ? updatedTicket : ticket));
    // Add to process log
    addProcessLog({
      action: 'Ticket Updated',
      details: `Updated ticket ${updatedTicket.ticketNumber} for ${updatedTicket.paxName}`
    });
    // Update reconciliation status
    updateReconciliationStatus();
  };
  // Handle deleting a ticket
  const handleDeleteTicket = () => {
    if (!selectedTicket) return;
    // Remove the ticket
    setTickets(tickets.filter(ticket => ticket.id !== selectedTicket.id));
    // Update group totals
    setGroupData(prev => ({
      ...prev,
      totalTickets: prev.totalTickets - 1,
      totalAmount: prev.totalAmount - selectedTicket.totalCost
    }));
    // Add to process log
    addProcessLog({
      action: 'Ticket Deleted',
      details: `Deleted ticket ${selectedTicket.ticketNumber} for ${selectedTicket.paxName}`
    });
    // Close modal and clear selection
    setIsDeleteModalOpen(false);
    setSelectedTicket(null);
    // Update reconciliation status
    updateReconciliationStatus();
  };
  // Handle adding an ATC
  const handleAddATC = newATC => {
    setAtcData([...atcData, newATC]);
    // Add to process log
    addProcessLog({
      action: 'ATC Added',
      details: `Added ${newATC.atcCode} (${newATC.description}) with amount $${newATC.amount.toFixed(2)}`
    });
  };
  // Handle deleting an ATC
  const handleDeleteATC = () => {
    if (!selectedATC) return;
    setAtcData(atcData.filter(item => item.id !== selectedATC.id));
    // Add to process log
    addProcessLog({
      action: 'ATC Deleted',
      details: `Deleted ${selectedATC.atcCode} (${selectedATC.description})`
    });
    // Close modal and clear selection
    setIsDeleteATCModalOpen(false);
    setSelectedATC(null);
  };
  // Handle marking tickets as reconciled
  const handleReconcileAll = () => {
    // Update all tickets to reconciled
    const updatedTickets = tickets.map(ticket => ({
      ...ticket,
      reconciled: true
    }));
    setTickets(updatedTickets);
    setReconciliationStatus('complete');
    // Add to process log
    addProcessLog({
      action: 'Reconciliation Complete',
      details: `Marked all tickets as reconciled`
    });
  };
  // Handle reconciling a single ticket
  const handleReconcileSingle = ticketId => {
    const updatedTickets = tickets.map(ticket => ticket.id === ticketId ? {
      ...ticket,
      reconciled: true
    } : ticket);
    setTickets(updatedTickets);
    // Add to process log
    const ticket = tickets.find(t => t.id === ticketId);
    if (ticket) {
      addProcessLog({
        action: 'Ticket Reconciled',
        details: `Reconciled ticket ${ticket.ticketNumber} for ${ticket.paxName}`
      });
    }
    // Update reconciliation status
    updateReconciliationStatus();
  };
  // Add a new process log entry
  const addProcessLog = ({
    action,
    details
  }) => {
    const newLog = {
      id: processLogs.length + 1,
      action,
      timestamp: new Date().toLocaleString(),
      user: 'John Doe',
      details
    };
    setProcessLogs([newLog, ...processLogs]);
  };
  // Update reconciliation status based on tickets
  const updateReconciliationStatus = () => {
    const reconciledCount = tickets.filter(t => t.reconciled).length;
    if (reconciledCount === 0) {
      setReconciliationStatus('pending');
    } else if (reconciledCount === tickets.length) {
      setReconciliationStatus('complete');
    } else {
      setReconciliationStatus('partial');
    }
  };
  // Handle printing
  const handlePrint = () => {
    window.print();
    // Add to process log
    addProcessLog({
      action: 'Document Printed',
      details: `Printed ticket group ${gsId}`
    });
  };
  // Calculate totals for the table footer
  const calculateTotals = () => {
    return tickets.reduce((acc, ticket) => {
      return {
        tax: acc.tax + ticket.tax,
        airFare: acc.airFare + ticket.airFare,
        totalCost: acc.totalCost + ticket.totalCost
      };
    }, {
      tax: 0,
      airFare: 0,
      totalCost: 0
    });
  };
  // Calculate ATC totals
  const calculateATCTotal = () => {
    return atcData.reduce((sum, item) => sum + item.amount, 0);
  };
  // Calculate billing total
  const calculateBillingTotal = () => {
    return billingEntries.reduce((sum, item) => sum + item.amount, 0);
  };
  // Auto-reconcile tickets with billing entries
  const handleAutoReconcile = () => {
    // Create copies of our state to modify
    const updatedTickets = [...tickets];
    const updatedBillingEntries = [...billingEntries];
    // Reset all reconciliation flags
    updatedTickets.forEach(ticket => {
      ticket.reconciled = false;
    });
    updatedBillingEntries.forEach(entry => {
      entry.matched = false;
    });
    // Try to match tickets with billing entries
    updatedTickets.forEach(ticket => {
      const matchingEntry = updatedBillingEntries.find(entry => entry.ticketNumber === ticket.ticketNumber);
      if (matchingEntry) {
        // Match found - check if amounts match
        if (Math.abs(ticket.totalCost - matchingEntry.amount) < 0.01) {
          // Exact match
          ticket.reconciled = true;
          matchingEntry.matched = true;
          // Add to process log
          addProcessLog({
            action: 'Auto-Reconciliation',
            details: `Auto-matched ticket ${ticket.ticketNumber} with billing entry`
          });
        }
      }
    });
    // Update state with reconciled data
    setTickets(updatedTickets);
    setBillingEntries(updatedBillingEntries);
    // Update reconciliation status
    updateReconciliationStatus();
  };
  // Handle manual matching of a system ticket with a billing entry
  const handleManualMatch = (systemTicketId, billingEntryId) => {
    // Find the ticket and billing entry
    const ticket = tickets.find(t => t.id === systemTicketId);
    const billingEntry = billingEntries.find(b => b.id === billingEntryId);
    if (!ticket || !billingEntry) return;
    // Create copies of our state to modify
    const updatedTickets = [...tickets];
    const updatedBillingEntries = [...billingEntries];
    // Update the matched ticket
    const ticketIndex = updatedTickets.findIndex(t => t.id === systemTicketId);
    updatedTickets[ticketIndex] = {
      ...updatedTickets[ticketIndex],
      reconciled: true
    };
    // Update the matched billing entry
    const entryIndex = updatedBillingEntries.findIndex(b => b.id === billingEntryId);
    updatedBillingEntries[entryIndex] = {
      ...updatedBillingEntries[entryIndex],
      matched: true
    };
    // Update state
    setTickets(updatedTickets);
    setBillingEntries(updatedBillingEntries);
    // Add to process log
    addProcessLog({
      action: 'Manual Reconciliation',
      details: `Manually matched ticket ${ticket.ticketNumber} with billing entry ${billingEntry.billingRef}`
    });
    // Update reconciliation status
    updateReconciliationStatus();
  };
  // Handle unmatching a reconciled ticket
  const handleUnmatchTicket = systemTicketId => {
    // Find the ticket
    const ticket = tickets.find(t => t.id === systemTicketId);
    if (!ticket) return;
    // Create copies of our state to modify
    const updatedTickets = [...tickets];
    const updatedBillingEntries = [...billingEntries];
    // Update the ticket
    const ticketIndex = updatedTickets.findIndex(t => t.id === systemTicketId);
    updatedTickets[ticketIndex] = {
      ...updatedTickets[ticketIndex],
      reconciled: false
    };
    // Find and update any matching billing entry
    const entryIndex = updatedBillingEntries.findIndex(b => b.ticketNumber === ticket.ticketNumber && b.matched);
    if (entryIndex !== -1) {
      updatedBillingEntries[entryIndex] = {
        ...updatedBillingEntries[entryIndex],
        matched: false
      };
    }
    // Update state
    setTickets(updatedTickets);
    setBillingEntries(updatedBillingEntries);
    // Add to process log
    addProcessLog({
      action: 'Reconciliation Removed',
      details: `Removed reconciliation for ticket ${ticket.ticketNumber}`
    });
    // Update reconciliation status
    updateReconciliationStatus();
  };
  // Handle uploading a billing file
  const handleUploadBillingFile = () => {
    setIsBillingFileUploaded(true);
    // Add to process log
    addProcessLog({
      action: 'Billing File Uploaded',
      details: `Uploaded billing file for reconciliation`
    });
  };
  // Calculate reconciliation statistics
  const calculateReconciliationStats = () => {
    const matchedCount = tickets.filter(t => t.reconciled).length;
    const mismatchCount = tickets.filter(t => {
      // Find matching billing entry
      const matchingEntry = billingEntries.find(b => b.ticketNumber === t.ticketNumber && b.matched);
      // If there's a match but amounts differ, it's a mismatch
      return matchingEntry && Math.abs(t.totalCost - matchingEntry.amount) >= 0.01;
    }).length;
    const unmatchedSystemTickets = tickets.filter(t => !t.reconciled).length;
    const unmatchedBillingEntries = billingEntries.filter(b => !b.matched).length;
    const systemTotal = tickets.reduce((sum, t) => sum + t.totalCost, 0);
    const billingTotal = billingEntries.reduce((sum, b) => sum + b.amount, 0);
    const varianceAmount = systemTotal - billingTotal;
    return {
      matchedCount,
      mismatchCount,
      unmatchedSystemTickets,
      unmatchedBillingEntries,
      systemTotal,
      billingTotal,
      varianceAmount
    };
  };
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedATC, setSelectedATC] = useState(null);
  const [isBillingFileUploaded, setIsBillingFileUploaded] = useState(false);
  const totals = calculateTotals();
  const reconciliationStats = calculateReconciliationStats();
  return <div className="space-y-6" data-id="element-1661">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1662">
        <div className="flex items-center gap-4" data-id="element-1663">
          <Link to="/tickets" className="text-gray-500 hover:text-gray-700" data-id="element-1664">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-1665" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1666">
            Ticket Group Details
          </h1>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-1667">
          <button className={`inline-flex items-center px-4 py-2 ${isLocked ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'} text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`} onClick={handleToggleLock} data-id="element-1668">
            <LockIcon className="h-5 w-5 mr-1" data-id="element-1669" />
            <span data-id="element-1670">{isLocked ? 'Unlock Group' : 'Lock Group'}</span>
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={handlePrint} data-id="element-1671">
            <PrinterIcon className="h-5 w-5 mr-1" data-id="element-1672" />
            <span data-id="element-1673">Print</span>
          </button>
        </div>
      </div>
      {isLocked && <PermissionBanner type="locked" data-id="element-1674" />}
      <div className="bg-white shadow rounded-lg p-6" data-id="element-1675">
        <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1676">
          Ticketing Group Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1677">
          <div data-id="element-1678">
            <div className="mb-4" data-id="element-1679">
              <p className="text-sm text-gray-500" data-id="element-1680">Issue Voucher No</p>
              <p className="font-medium" data-id="element-1681">{groupData.id}</p>
            </div>
            <div data-id="element-1682">
              <p className="text-sm text-gray-500" data-id="element-1683">Created By</p>
              <p className="font-medium" data-id="element-1684">{groupData.createdBy}</p>
            </div>
          </div>
          <div data-id="element-1685">
            <div className="mb-4" data-id="element-1686">
              <p className="text-sm text-gray-500" data-id="element-1687">Tour Code</p>
              <p className="font-medium" data-id="element-1688">{groupData.tourCode}</p>
            </div>
            <div data-id="element-1689">
              <p className="text-sm text-gray-500" data-id="element-1690">Created Date</p>
              <p className="font-medium" data-id="element-1691">{groupData.createdAt}</p>
            </div>
          </div>
          <div data-id="element-1692">
            <div className="mb-4" data-id="element-1693">
              <p className="text-sm text-gray-500" data-id="element-1694">Request Ref</p>
              <p className="font-medium" data-id="element-1695">{groupData.refNo}</p>
            </div>
            <div className="flex items-center justify-between" data-id="element-1696">
              <div data-id="element-1697">
                <p className="text-sm text-gray-500" data-id="element-1698">Status</p>
                <StatusBadge status={groupData.status === 'Locked' ? 'locked' : 'success'} label={groupData.status} data-id="element-1699" />
              </div>
              <div data-id="element-1700">
                <p className="text-sm text-gray-500" data-id="element-1701">Reconciliation</p>
                <StatusBadge status={reconciliationStatus === 'complete' ? 'success' : reconciliationStatus === 'partial' ? 'warning' : 'pending'} label={reconciliationStatus === 'complete' ? 'Complete' : reconciliationStatus === 'partial' ? 'Partial' : 'Pending'} data-id="element-1702" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-1703">
        <nav className="-mb-px flex space-x-8" data-id="element-1704">
          <button onClick={() => setActiveTab('ticket')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'ticket' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1705">
            Ticket
          </button>
          <button onClick={() => setActiveTab('atc')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'atc' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1706">
            ATC
          </button>
          <button onClick={() => setActiveTab('reconciliation')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'reconciliation' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1707">
            Reconciliation
          </button>
          <button onClick={() => setActiveTab('processLog')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'processLog' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1708">
            Process Log
          </button>
        </nav>
      </div>
      {activeTab === 'ticket' && <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1709">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-1710">Tickets Records</h2>
            <div className="flex flex-wrap gap-2" data-id="element-1711">
              <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => setIsSingleTicketModalOpen(true)} disabled={isLocked} data-id="element-1712">
                <PlusIcon className="h-5 w-5 mr-1" data-id="element-1713" />
                <span data-id="element-1714">Add Single Ticket</span>
              </button>
              <button className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" onClick={() => setIsBatchModalOpen(true)} disabled={isLocked} data-id="element-1715">
                <ClipboardListIcon className="h-5 w-5 mr-1" data-id="element-1716" />
                <span data-id="element-1717">Batch Add Tickets</span>
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => {
            // In a real app, this would trigger an export function
            console.log('Exporting tickets data');
            // Add to process log
            addProcessLog({
              action: 'Export',
              details: `Exported ticket data for group ${gsId}`
            });
          }} data-id="element-1718">
                <FileDownIcon className="h-5 w-5 mr-1" data-id="element-1719" />
                <span data-id="element-1720">Export Tickets</span>
              </button>
            </div>
          </div>
          <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-1721">
            <div className="p-4 border-b border-gray-200" data-id="element-1722">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1723">
                <div data-id="element-1724">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1725">
                    Search Tkt By PnrNo
                  </label>
                  <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1726" />
                </div>
                <div data-id="element-1727">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1728">
                    Issue Vou No
                  </label>
                  <input type="text" value={gsId} disabled className="block w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md leading-5 text-gray-500 sm:text-sm" data-id="element-1729" />
                </div>
                <div data-id="element-1730">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1731">
                    Item No
                  </label>
                  <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1732" />
                </div>
                <div data-id="element-1733">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1734">
                    Request No
                  </label>
                  <input type="text" value={groupData.refNo} disabled className="block w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md leading-5 text-gray-500 sm:text-sm" data-id="element-1735" />
                </div>
              </div>
            </div>
            {tickets.length > 0 ? <div className="overflow-x-auto" data-id="element-1736">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1737">
                  <thead className="bg-gray-50" data-id="element-1738">
                    <tr data-id="element-1739">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1740">
                        Ticket Number
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1741">
                        Pax Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1742">
                        Sector Code
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1743">
                        Sector
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1744">
                        A/L Code
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1745">
                        Class
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1746">
                        Issue Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1747">
                        Tkt Tax
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1748">
                        Air Fare
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1749">
                        Total Cost
                      </th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1750">
                        Recon
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1751">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1752">
                    {tickets.map((ticket, index) => <tr key={ticket.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`} data-id="element-1753">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1754">
                          {ticket.ticketNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1755">
                          {ticket.paxName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1756">
                          {ticket.sectorCode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1757">
                          {ticket.sector}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1758">
                          {ticket.airlineCode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1759">
                          {ticket.class}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1760">
                          {ticket.issueDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1761">
                          $
                          {ticket.tax.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1762">
                          $
                          {ticket.airFare.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1763">
                          $
                          {ticket.totalCost.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1764">
                          {ticket.reconciled ? <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" data-id="element-1765" /> : <button onClick={() => handleReconcileSingle(ticket.id)} className="text-red-500 hover:text-green-500" data-id="element-1766">
                              <XIcon className="h-5 w-5 mx-auto" data-id="element-1767" />
                            </button>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2" data-id="element-1768">
                          {!isLocked && <>
                              <button className="text-blue-600 hover:text-blue-900" onClick={() => {
                      setSelectedTicket(ticket);
                      setIsEditModalOpen(true);
                    }} data-id="element-1769">
                                Edit
                              </button>
                              <button className="text-red-600 hover:text-red-900" onClick={() => {
                      setSelectedTicket(ticket);
                      setIsDeleteModalOpen(true);
                    }} data-id="element-1770">
                                Delete
                              </button>
                            </>}
                          <button className="text-blue-600 hover:text-blue-900" onClick={() => {
                    setSelectedTicket(ticket);
                    setIsViewModalOpen(true);
                  }} data-id="element-1771">
                            View
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                  <tfoot className="bg-gray-100" data-id="element-1772">
                    <tr data-id="element-1773">
                      <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-1774">
                        Summary Totals:
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1775">
                        $
                        {totals.tax.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1776">
                        $
                        {totals.airFare.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1777">
                        $
                        {totals.totalCost.toLocaleString('en-US', {
                    minimumFractionDigits: 2
                  })}
                      </td>
                      <td colSpan={2} data-id="element-1778"></td>
                    </tr>
                  </tfoot>
                </table>
              </div> : <div className="py-8" data-id="element-1779">
                <EmptyState title="No tickets found" description="Add your first ticket to this group" icon={<FileTextIcon className="h-8 w-8 text-gray-400" data-id="element-1781" />} actionLabel={isLocked ? undefined : 'Add Ticket'} actionLink={isLocked ? undefined : '#'} data-id="element-1780" />
              </div>}
          </div>
        </>}
      {activeTab === 'atc' && <div className="bg-white shadow rounded-lg p-6" data-id="element-1782">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-1783">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-1784">ATC Details</h3>
            {!isLocked && <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => setIsAddATCModalOpen(true)} data-id="element-1785">
                <PlusIcon className="h-5 w-5 mr-1" data-id="element-1786" />
                <span data-id="element-1787">Add ATC</span>
              </button>}
          </div>
          {atcData.length > 0 ? <div className="overflow-x-auto" data-id="element-1788">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1789">
                <thead className="bg-gray-50" data-id="element-1790">
                  <tr data-id="element-1791">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1792">
                      ATC Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1793">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1794">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1795">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1796">
                  {atcData.map((item, index) => <tr key={item.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`} data-id="element-1797">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1798">
                        {item.atcCode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1799">
                        {item.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1800">
                        $
                        {item.amount.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2" data-id="element-1801">
                        {!isLocked && <>
                            <button className="text-red-600 hover:text-red-900" onClick={() => {
                    setSelectedATC(item);
                    setIsDeleteATCModalOpen(true);
                  }} data-id="element-1802">
                              <TrashIcon className="h-4 w-4 inline" data-id="element-1803" />
                            </button>
                          </>}
                      </td>
                    </tr>)}
                </tbody>
                <tfoot className="bg-gray-100" data-id="element-1804">
                  <tr data-id="element-1805">
                    <td colSpan={2} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right" data-id="element-1806">
                      Total ATC Amount:
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1807">
                      $
                      {calculateATCTotal().toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                    </td>
                    <td data-id="element-1808"></td>
                  </tr>
                </tfoot>
              </table>
            </div> : <div className="py-8" data-id="element-1809">
              <EmptyState title="No ATC charges found" description="Add additional ticket charges if needed" icon={<CreditCardIcon className="h-8 w-8 text-gray-400" data-id="element-1811" />} actionLabel={isLocked ? undefined : 'Add ATC'} actionLink={isLocked ? undefined : '#'} data-id="element-1810" />
            </div>}
        </div>}
      {activeTab === 'reconciliation' && <div className="space-y-6" data-id="element-1812">
          {!isBillingFileUploaded ? <div className="bg-white shadow rounded-lg p-6" data-id="element-1813">
              <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-1814">
                Upload Billing File for Reconciliation
              </h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center" data-id="element-1815">
                <div className="flex flex-col items-center" data-id="element-1816">
                  <FileUpIcon className="h-12 w-12 text-gray-400 mb-4" data-id="element-1817" />
                  <p className="text-lg font-medium text-gray-900 mb-1" data-id="element-1818">
                    Upload Billing File
                  </p>
                  <p className="text-sm text-gray-500 mb-4" data-id="element-1819">
                    Upload a billing file to start the reconciliation process
                  </p>
                  <button onClick={handleUploadBillingFile} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-1820">
                    <FileUpIcon className="h-5 w-5 mr-1" data-id="element-1821" />
                    Upload Billing File
                  </button>
                </div>
              </div>
            </div> : <>
              <ReconciliationSummary totalSystemTickets={tickets.length} totalBillingEntries={billingEntries.length} matchedCount={reconciliationStats.matchedCount} mismatchCount={reconciliationStats.mismatchCount} unmatchedSystemTickets={reconciliationStats.unmatchedSystemTickets} unmatchedBillingEntries={reconciliationStats.unmatchedBillingEntries} systemTotal={reconciliationStats.systemTotal} billingTotal={reconciliationStats.billingTotal} varianceAmount={reconciliationStats.varianceAmount} onAutoReconcile={handleAutoReconcile} data-id="element-1822" />
              <div className="bg-white shadow rounded-lg p-6" data-id="element-1823">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-1824">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-1825">
                    Reconciliation Details
                  </h3>
                  <div className="flex gap-2" data-id="element-1826">
                    <button onClick={() => setIsBillingFileUploaded(false)} className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-1827">
                      <FileUpIcon className="h-5 w-5 mr-1" data-id="element-1828" />
                      <span data-id="element-1829">Upload New File</span>
                    </button>
                    <button onClick={handleAutoReconcile} className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-1830">
                      <RefreshCwIcon className="h-5 w-5 mr-1" data-id="element-1831" />
                      <span data-id="element-1832">Retry Auto-Match</span>
                    </button>
                  </div>
                </div>
                <ReconciliationTable systemTickets={tickets} billingEntries={billingEntries} onManualMatch={handleManualMatch} onUnmatch={handleUnmatchTicket} data-id="element-1833" />
              </div>
            </>}
        </div>}
      {activeTab === 'processLog' && <div className="bg-white shadow rounded-lg p-6" data-id="element-1834">
          <h3 className="text-lg font-medium text-gray-900 mb-6" data-id="element-1835">
            Process Log
          </h3>
          <div className="relative" data-id="element-1836">
            <div className="absolute left-4 inset-y-0 w-0.5 bg-gray-200" data-id="element-1837"></div>
            <ul className="space-y-6" data-id="element-1838">
              {processLogs.map(log => <li key={log.id} className="relative pl-10" data-id="element-1839">
                  <div className="absolute left-0 top-1 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-500 border-4 border-white" data-id="element-1840">
                    <HistoryIcon className="h-4 w-4" data-id="element-1841" />
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-1842">
                    <div className="flex items-center justify-between mb-2" data-id="element-1843">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-1844">
                        {log.action}
                      </h4>
                      <time className="text-xs text-gray-500" data-id="element-1845">
                        {log.timestamp}
                      </time>
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-1846">{log.details}</p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1847">By: {log.user}</p>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>}
      {/* Batch Ticket Entry Modal */}
      {isBatchModalOpen && <BatchTicketEntryModal onClose={() => setIsBatchModalOpen(false)} groupId={gsId} data-id="element-1848" />}
      {/* Single Ticket Entry Modal */}
      {isSingleTicketModalOpen && <SingleTicketEntryModal onClose={() => setIsSingleTicketModalOpen(false)} groupId={gsId} onSave={handleAddTicket} data-id="element-1849" />}
      {/* View Ticket Modal */}
      {isViewModalOpen && selectedTicket && <ViewTicketModal onClose={() => {
      setIsViewModalOpen(false);
      setSelectedTicket(null);
    }} onEdit={() => {
      setIsViewModalOpen(false);
      setIsEditModalOpen(true);
    }} ticket={selectedTicket} isLocked={isLocked} data-id="element-1850" />}
      {/* Edit Ticket Modal */}
      {isEditModalOpen && selectedTicket && <EditTicketModal onClose={() => {
      setIsEditModalOpen(false);
      setSelectedTicket(null);
    }} onSave={handleUpdateTicket} ticket={selectedTicket} data-id="element-1851" />}
      {/* Add ATC Modal */}
      {isAddATCModalOpen && <AddATCModal onClose={() => setIsAddATCModalOpen(false)} onSave={handleAddATC} data-id="element-1852" />}
      {/* Confirm Delete Ticket Modal */}
      {isDeleteModalOpen && selectedTicket && <ConfirmDeleteModal onClose={() => {
      setIsDeleteModalOpen(false);
      setSelectedTicket(null);
    }} onConfirm={handleDeleteTicket} title="Delete Ticket" message="Are you sure you want to delete this ticket? This action cannot be undone." itemName={`${selectedTicket.ticketNumber} - ${selectedTicket.paxName}`} data-id="element-1853" />}
      {/* Confirm Delete ATC Modal */}
      {isDeleteATCModalOpen && selectedATC && <ConfirmDeleteModal onClose={() => {
      setIsDeleteATCModalOpen(false);
      setSelectedATC(null);
    }} onConfirm={handleDeleteATC} title="Delete ATC" message="Are you sure you want to delete this ATC entry? This action cannot be undone." itemName={`${selectedATC.atcCode} - ${selectedATC.description}`} data-id="element-1854" />}
    </div>;
};
export default TicketManagement;